/**
 * Camera + cursor interpolation for browse scenes. Pure math, frame-driven.
 *
 * The camera is defined by keyframes {at, x, y, zoom}: at time `at`, the shot
 * point (x, y) sits at frame center, scaled so zoom 1 = shot width fits the
 * frame width. Between keyframes we ease pan and zoom (zoom in log space so
 * push-ins feel linear to the eye), and clamp so the frame never shows past
 * the shot's edges.
 *
 * No springs: camera arrivals decelerate and stop dead — overshoot reads as
 * bounce when it's scaling a full screenshot.
 */
import { clamp01, mix, easeInOutCubic, easeOutExpo, easeOutQuint } from '@/lib/easing'
import type { DemoCameraKey, DemoCursorStep } from './types'

const EASES: Record<string, (t: number) => number> = {
  inOut: easeInOutCubic,
  out: easeOutQuint,
  settle: easeOutExpo,
  linear: (t: number) => t,
}

export type CameraPose = { x: number; y: number; zoom: number }

function interpolateKeys(keys: DemoCameraKey[], tSec: number): CameraPose {
  if (keys.length === 0) return { x: 0, y: 0, zoom: 1 }
  if (tSec <= keys[0].at) return keys[0]
  const last = keys[keys.length - 1]
  if (tSec >= last.at) return last
  let i = 0
  while (i < keys.length - 1 && keys[i + 1].at <= tSec) i++
  const a = keys[i]
  const b = keys[i + 1]
  const span = Math.max(0.001, b.at - a.at)
  const t = (EASES[b.ease ?? 'inOut'] ?? easeInOutCubic)(clamp01((tSec - a.at) / span))
  return {
    x: mix(a.x, b.x, t),
    y: mix(a.y, b.y, t),
    zoom: Math.exp(mix(Math.log(a.zoom), Math.log(b.zoom), t)),
  }
}

export type CameraTransform = {
  /** translate (px) applied before scale, transform-origin 0 0 */
  tx: number
  ty: number
  scale: number
}

/**
 * Resolve the camera at tSec into a CSS transform for the shot image.
 * frameW/frameH: viewport of the browser-chrome content area.
 * shotW/shotH: CSS-px size of the shot.
 */
export function resolveCamera(
  keys: DemoCameraKey[] | undefined,
  tSec: number,
  frameW: number,
  frameH: number,
  shotW: number,
  shotH: number,
): CameraTransform {
  const pose = interpolateKeys(
    keys && keys.length > 0 ? keys : [{ at: 0, x: shotW / 2, y: frameH / 2, zoom: 1 }],
    tSec,
  )
  const base = frameW / shotW // zoom 1 = fit width
  // Subtle breathing so holds never feel frozen.
  const drift = 1 + 0.006 * Math.sin(tSec * 0.55)
  const scale = base * pose.zoom * drift
  let tx = frameW / 2 - pose.x * scale
  let ty = frameH / 2 - pose.y * scale
  // Clamp: never show past shot edges (when the scaled shot covers the frame).
  const minTx = frameW - shotW * scale
  const minTy = frameH - shotH * scale
  tx = minTx < 0 ? Math.min(0, Math.max(minTx, tx)) : minTx / 2
  ty = minTy < 0 ? Math.min(0, Math.max(minTy, ty)) : minTy / 2
  return { tx, ty, scale }
}

/** Map a shot-space point to frame space under a camera transform. */
export function toFrame(p: { x: number; y: number }, cam: CameraTransform) {
  return { x: p.x * cam.scale + cam.tx, y: p.y * cam.scale + cam.ty }
}

export type CursorPose = {
  x: number
  y: number
  /** 0..1 click pulse (1 at click moment, decays) */
  click: number
  /** cursor scale dip while pressing */
  press: number
  visible: boolean
}

/**
 * Cursor path: curved easings between steps (quadratic bezier with a
 * perpendicular bow that alternates side), click pulses at click steps.
 * Coordinates are shot-space; map with toFrame() for rendering.
 */
export function resolveCursor(steps: DemoCursorStep[] | undefined, tSec: number): CursorPose {
  if (!steps || steps.length === 0) return { x: 0, y: 0, click: 0, press: 1, visible: false }
  let pos: { x: number; y: number }
  if (tSec <= steps[0].at) {
    pos = steps[0]
  } else if (tSec >= steps[steps.length - 1].at) {
    pos = steps[steps.length - 1]
  } else {
    let i = 0
    while (i < steps.length - 1 && steps[i + 1].at <= tSec) i++
    const a = steps[i]
    const b = steps[i + 1]
    const span = Math.max(0.001, b.at - a.at)
    const t = easeInOutCubic(clamp01((tSec - a.at) / span))
    const dx = b.x - a.x
    const dy = b.y - a.y
    const dist = Math.hypot(dx, dy)
    const bow = Math.min(60, dist * 0.16) * (i % 2 === 0 ? 1 : -1)
    // Quadratic bezier: control = midpoint + perpendicular * bow
    const cx = (a.x + b.x) / 2 + (-dy / (dist || 1)) * bow
    const cy = (a.y + b.y) / 2 + (dx / (dist || 1)) * bow
    const u = 1 - t
    pos = {
      x: u * u * a.x + 2 * u * t * cx + t * t * b.x,
      y: u * u * a.y + 2 * u * t * cy + t * t * b.y,
    }
  }
  // Click pulse: strongest at the click step's time, decays over 0.6s.
  let click = 0
  let press = 1
  for (const s of steps) {
    if (!s.click) continue
    const dt = tSec - s.at
    if (dt >= 0 && dt < 0.6) click = Math.max(click, 1 - dt / 0.6)
    if (dt >= -0.08 && dt < 0.18) press = 0.86
  }
  return { ...pos, click, press, visible: true }
}
